import JsonView from '@uiw/react-json-view';
import React from 'react';
import CopyIcon from './CustomIcons/CopyIcon';
import { type DiffDisplayRow, type DiffRowType, diffHunks } from './codeBlockDiff';
import { CodeLine, type CodeLineDecoration } from './codeBlockLine';
import { FOCUS_RING } from './focusRing';

const CodeBlockHighlighter = React.lazy(() => import('./CodeBlockHighlighter'));

export interface CodeBlockProps {
    /** The code content to display */
    code?: string;
    /** JSON object to display as formatted JSON */
    json?: object;
    /** Show line numbers */
    showLineNumbers?: boolean;
    /** Custom class name */
    className?: string;
    /** Custom styles */
    style?: React.CSSProperties;
    /** Height of the code block */
    height?: string | number;
    /** Enable copy functionality */
    enableCopy?: boolean;
    /** Wrap long lines instead of scrolling horizontally */
    wrapLines?: boolean;
    /**
     * Highlight the code with this language's grammar. `cedar` is a grammar this library
     * ships itself (`prismCedar.ts`), since Prism and refractor carry none; widen the union
     * as further grammars are registered in CodeBlockHighlighter.
     */
    language?: 'yaml' | 'cedar';
    /** How much of a `json` tree starts collapsed: `true` all of it, a number below that depth. */
    collapsed?: boolean | number;
    /**
     * Renders the change between two texts rather than one text, and takes precedence over
     * `code` and `json`. Only the changed lines are shown, with `context` unchanged ones either
     * side, so a small edit to a long document reads as a few hunks instead of the whole thing.
     */
    diff?: {
        before: string;
        after: string;
        /** Unchanged lines kept either side of each change. Three by convention. */
        context?: number;
        /**
         * Swaps the `+`/`-` gutter and tinted rows for the palette a mock specified for
         * AI-authored changes: star, minus-sign and middle-dot markers, and a struck-through
         * removal. Omit to keep the neutral markers; there is no default consumer for the
         * assistant look yet.
         */
        origin?: 'assistant';
    };
}

const DIFF_BACKGROUNDS: Record<DiffRowType, string | undefined> = {
    added: 'var(--malachite-codeblock-diff-added-background)',
    removed: 'var(--malachite-codeblock-diff-removed-background)',
    context: undefined,
};

const DIFF_MARKERS: Record<DiffRowType, string | undefined> = {
    added: '+',
    removed: '-',
    context: undefined,
};

// The assistant palette also marks context rows, which the neutral one leaves bare.
const ASSISTANT_DIFF_BACKGROUNDS: Record<DiffRowType, string | undefined> = {
    added: 'var(--malachite-codeblock-diff-assistant-background)',
    removed: 'var(--malachite-codeblock-diff-assistant-removed-background)',
    context: undefined,
};

const ASSISTANT_DIFF_MARKERS: Record<DiffRowType, string> = {
    added: '\u2726',
    // U+2212 minus sign, not a hyphen: the mock is explicit about which glyph.
    removed: '\u2212',
    context: '\u00b7',
};

const ASSISTANT_DIFF_MARKER_COLORS: Record<DiffRowType, string> = {
    added: 'var(--malachite-codeblock-diff-assistant-foreground)',
    removed: 'var(--malachite-codeblock-diff-removed-foreground)',
    context: 'var(--malachite-codeblock-diff-context-foreground)',
};

const unchangedRun = (count: number): string =>
    `\u22ef ${count} unchanged ${count === 1 ? 'line' : 'lines'}`;

const diffDecoration = (
    row: DiffDisplayRow,
    showLineNumbers: boolean,
    origin?: 'assistant',
): CodeLineDecoration => {
    const assistant = origin === 'assistant';

    return {
        background: assistant ? ASSISTANT_DIFF_BACKGROUNDS[row.type] : DIFF_BACKGROUNDS[row.type],
        marker: assistant ? ASSISTANT_DIFF_MARKERS[row.type] : DIFF_MARKERS[row.type],
        markerColor: assistant ? ASSISTANT_DIFF_MARKER_COLORS[row.type] : undefined,
        // Reserved on unchanged rows too, so the code column does not step in and out.
        reserveMarker: true,
        // Context rows read as de-emphasised, not just unmarked, in the assistant palette.
        textColor:
            assistant && row.type === 'context'
                ? 'var(--malachite-codeblock-diff-context-text)'
                : undefined,
        strikethrough: assistant && row.type === 'removed',
        separatorAbove: row.skippedAbove === undefined ? undefined : unchangedRun(row.skippedAbove),
        // A removed line has no number in the after-text, so it keeps the one it had before.
        lineNumber: showLineNumbers ? (row.afterNumber ?? row.beforeNumber) : undefined,
    };
};

const CodeBlock: React.FC<CodeBlockProps> = ({
    code,
    json,
    showLineNumbers = true,
    className = '',
    style = {},
    height,
    enableCopy = true,
    wrapLines = false,
    language,
    collapsed = false,
    diff,
}) => {
    const diffRows = diff ? diffHunks(diff.before, diff.after, diff.context) : null;
    const diffText = diffRows?.map((row) => row.text).join('\n') ?? '';

    /**
     * What the button puts on the clipboard, in the same form the block is showing. A JSON
     * block is pretty-printed rather than handed over minified: the reader is copying what
     * they can see. `undefined` means there is nothing to copy and no button to offer.
     */
    const copyText = (): string | undefined => {
        if (diffRows) {
            return diffText;
        }

        // An empty block has nothing to copy, so it offers no button — `!== undefined` would
        // hand back the empty string and put a dead control on it.
        if (code) {
            return code;
        }

        if (json !== undefined) {
            try {
                return JSON.stringify(json, null, 2);
            } catch {
                // A cycle or a BigInt: no honest text to copy, so offer no button.
                return undefined;
            }
        }

        return undefined;
    };

    const textToCopy = copyText();

    // `?.` no-ops when the clipboard API is absent (insecure context); `.catch` swallows a rejection.
    const handleCopy = () => {
        if (textToCopy === undefined) {
            return;
        }

        navigator.clipboard?.writeText(textToCopy)?.catch(() => undefined);
    };

    // Declared once and rendered by both the JSON branch and the code branch below, so the
    // two tabs of one document do not disagree about whether it can be copied.
    const copyButton = enableCopy && textToCopy !== undefined && (
        <button
            type="button"
            aria-label="Copy"
            onClick={handleCopy}
            className={`absolute top-2 right-2 z-10 flex items-center justify-center rounded-md border border-solid border-[var(--malachite-codeblock-border)] bg-[var(--malachite-codeblock-background)] p-1.5 transition-colors hover:bg-[var(--malachite-codeblock-border)] ${FOCUS_RING}`}
            style={{ color: 'var(--malachite-codeblock-line-number)' }}
        >
            <CopyIcon size={14} />
        </button>
    );

    // If JSON is provided, use JsonView component
    if (json && !diffRows) {
        return (
            <div
                className={`relative border rounded-lg overflow-hidden ${className}`}
                style={{
                    backgroundColor: 'var(--malachite-codeblock-background)',
                    borderColor: 'var(--malachite-codeblock-border)',
                    height,
                    ...style,
                }}
            >
                {copyButton}
                <JsonView
                    value={json}
                    style={
                        {
                            backgroundColor: 'var(--malachite-codeblock-background)',
                            color: 'var(--malachite-codeblock-foreground)',
                            fontSize: '14px',
                            fontFamily: 'var(--malachite-font-mono, monospace)',
                            padding: '16px',
                            lineHeight: '1.5',
                            textAlign: 'left',
                            '--w-rjv-color': 'var(--malachite-codeblock-foreground)',
                            '--w-rjv-key-string': 'var(--malachite-codeblock-keyword)',
                            '--w-rjv-background-color': 'var(--malachite-codeblock-background)',
                            '--w-rjv-line-color': 'var(--malachite-codeblock-border)',
                            '--w-rjv-arrow-color': 'var(--malachite-codeblock-line-number)',
                            '--w-rjv-edit-color': 'var(--malachite-codeblock-keyword)',
                            '--w-rjv-info-color': 'var(--malachite-codeblock-line-number)',
                            '--w-rjv-update-color': 'var(--malachite-codeblock-keyword)',
                            '--w-rjv-copied-color': 'var(--malachite-codeblock-string)',
                            '--w-rjv-copied-success-color': 'var(--malachite-codeblock-string)',
                            '--w-rjv-curlybraces-color': 'var(--malachite-codeblock-foreground)',
                            '--w-rjv-colon-color': 'var(--malachite-codeblock-foreground)',
                            '--w-rjv-brackets-color': 'var(--malachite-codeblock-foreground)',
                            '--w-rjv-quotes-color': 'var(--malachite-codeblock-string)',
                            '--w-rjv-quotes-string-color': 'var(--malachite-codeblock-string)',
                            '--w-rjv-type-string-color': 'var(--malachite-codeblock-string)',
                            '--w-rjv-type-int-color': 'var(--malachite-codeblock-number)',
                            '--w-rjv-type-float-color': 'var(--malachite-codeblock-number)',
                            '--w-rjv-type-bigint-color': 'var(--malachite-codeblock-number)',
                            '--w-rjv-type-boolean-color': 'var(--malachite-codeblock-keyword)',
                            '--w-rjv-type-date-color': 'var(--malachite-codeblock-string)',
                            '--w-rjv-type-url-color': 'var(--malachite-codeblock-keyword)',
                            '--w-rjv-type-null-color': 'var(--malachite-codeblock-comment)',
                            '--w-rjv-type-nan-color': 'var(--malachite-codeblock-comment)',
                            '--w-rjv-type-undefined-color': 'var(--malachite-codeblock-comment)',
                        } as React.CSSProperties
                    }
                    displayDataTypes={false}
                    enableClipboard={enableCopy}
                    collapsed={collapsed}
                    displayObjectSize={false}
                />
            </div>
        );
    }

    // Reserve clearance so a wide first line never sits under the floating copy button.
    const copyClearance = enableCopy && (!!code || !!diffRows);

    const renderPlainLines = (
        text: string,
        decorate?: (index: number) => CodeLineDecoration | undefined,
    ) => {
        const lines = text.split('\n');

        return (
            <div
                className="p-4 overflow-auto text-left"
                style={{ paddingRight: copyClearance ? 44 : undefined }}
            >
                {/* As wide as the widest line, floored at the view, so rows filling it end level. */}
                <div style={{ width: 'max-content', minWidth: '100%' }}>
                    {lines.map((line, index) => {
                        const decoration = decorate?.(index);

                        return (
                            <CodeLine
                                key={index}
                                line={line}
                                lineNumber={
                                    decoration?.lineNumber ??
                                    (showLineNumbers ? index + 1 : undefined)
                                }
                                wrap={wrapLines}
                                decoration={decoration}
                            >
                                {line}
                            </CodeLine>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderPlainCode = () => (code ? renderPlainLines(code) : null);

    const renderDiff = (rows: DiffDisplayRow[]) => {
        if (rows.length === 0) {
            return (
                <div
                    className="p-4 text-left"
                    style={{ color: 'var(--malachite-codeblock-comment)' }}
                >
                    No changes
                </div>
            );
        }

        const decorate = (index: number) => {
            const row = rows[index];

            return row ? diffDecoration(row, showLineNumbers, diff?.origin) : undefined;
        };

        if (language) {
            return (
                <React.Suspense fallback={renderPlainLines(diffText, decorate)}>
                    {/*
                     * The whole merged text goes to the highlighter in one piece, so a hunk
                     * is tokenised in the company of its neighbours rather than on its own.
                     * Prism still cannot see the lines the hunks left out, so a construct
                     * spanning an omitted run can highlight oddly — the reason `context`
                     * defaults to a few lines rather than none.
                     */}
                    <CodeBlockHighlighter
                        code={diffText}
                        language={language}
                        showLineNumbers={showLineNumbers}
                        wrapLines={wrapLines}
                        copyClearance={copyClearance}
                        decorate={decorate}
                    />
                </React.Suspense>
            );
        }

        return renderPlainLines(diffText, decorate);
    };

    // For code strings, create a simple syntax-highlighted view
    const renderCodeContent = () => {
        if (!code) {
            return null;
        }

        if (language) {
            return (
                <React.Suspense fallback={renderPlainCode()}>
                    <CodeBlockHighlighter
                        code={code}
                        language={language}
                        showLineNumbers={showLineNumbers}
                        wrapLines={wrapLines}
                        copyClearance={copyClearance}
                    />
                </React.Suspense>
            );
        }

        return renderPlainCode();
    };

    return (
        <div
            className={`relative border rounded-lg overflow-hidden ${className}`}
            style={{
                backgroundColor: 'var(--malachite-codeblock-background)',
                borderColor: 'var(--malachite-codeblock-border)',
                height,
                ...style,
            }}
        >
            {copyButton}
            {diffRows ? renderDiff(diffRows) : renderCodeContent()}
        </div>
    );
};

export default CodeBlock;
